import { Accordion, Alert, Button, Chip, Icon, Text } from "@assetwatch/ui";
import { useTranslation } from "react-i18next";
import type {
  MarketplaceResponse,
  SelectedMarketplaceRequest,
} from "../../types/marketplace";
import { ActiveSubscriptionCard } from "./ActiveSubscriptionCard";

type ActiveSubscriptionsSectionProps = {
  marketplace: MarketplaceResponse;
  onViewAll: () => void;
  onSelect: (request: SelectedMarketplaceRequest) => void;
};

export function ActiveSubscriptionsSection({
  marketplace,
  onViewAll,
  onSelect,
}: ActiveSubscriptionsSectionProps) {
  const { t } = useTranslation();
  const { activeSubscriptions, availableCapabilities, customer } = marketplace;

  return (
    <Accordion
      title={
        <span className="inline-flex items-center gap-(--spacing-8)">
          <span>{t("marketplace.activeSubscriptions")}</span>
          <Text size="sm" tone="subtle">
            {t("marketplace.activeCount", {
              count: activeSubscriptions.length,
            })}
          </Text>
        </span>
      }
      defaultOpen
      actions={
        <Button variant="outline" size="sm" onClick={onViewAll}>
          <span className="flex items-center gap-(--spacing-8)">
            <Icon name="marketplace" size="sm" tone="inherit" aria-hidden />
            <span>{t("marketplace.viewAllSubscriptions")}</span>
          </span>
          <Chip
            tone="positive"
            label={activeSubscriptions.length + availableCapabilities.length}
          />
        </Button>
      }
      summary={
        <Text tone="subtle">
          {t("marketplace.activeSubscriptionsDescription", {
            customerName: customer.customerName,
          })}
        </Text>
      }
    >
      {activeSubscriptions.length === 0 ? (
        <Alert
          tone="informative"
          title={t("marketplace.noActiveSubscriptionsTitle")}
          subtext={t("marketplace.noActiveSubscriptionsDescription")}
        />
      ) : (
        <div className="grid gap-(--spacing-16) md:grid-cols-2 lg:grid-cols-3">
          {activeSubscriptions.map((subscription) => (
            <ActiveSubscriptionCard
              key={subscription.id}
              subscription={subscription}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </Accordion>
  );
}
